import React, { useEffect, useMemo, useRef } from 'react';
import { Chart } from 'chart.js/auto';

// ↓ reusable hook untuk Chart.js
function useChart(canvasRef, deps, buildConfig) {
  const chartRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const config = buildConfig();
    if (!config) return;

    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(canvas, config);

    return () => chartRef.current?.destroy();
    // eslint-disable-next-line
  }, deps);
}

export default function ProvincieInsight({
  domicilies = [],
  loading = false,
  showTable = false,
  showInsight = false,
}) {
  const top10Ref = useRef(null);
  const tierRef = useRef(null);

  // ─────────────────────────────────────────────
  // NORMALIZE + SORT
  // ─────────────────────────────────────────────
  const data = useMemo(() => {
    if (!Array.isArray(domicilies)) return [];

    return [...domicilies]
      .map((d) => ({
        province_name: d?.province_name ?? null,
        total_users: Number(d?.total_users ?? 0),
      }))
      .sort((a, b) => b.total_users - a.total_users);
  }, [domicilies]);

  const totalUsers = useMemo(
    () => data.reduce((sum, d) => sum + d.total_users, 0),
    [data]
  );

  const top10 = data.slice(0, 10);
  const top1 = data[0];

  // ─────────────────────────────────────────────
  // TIER GROUPING (Provinsi)
  // ─────────────────────────────────────────────
  const tiers = useMemo(() => {
    const t1 = data.filter((d) => d.total_users > 300).length;
    const t2 = data.filter(
      (d) => d.total_users >= 150 && d.total_users <= 300
    ).length;
    const t3 = data.filter(
      (d) => d.total_users >= 50 && d.total_users <= 149
    ).length;
    const t4 = data.filter((d) => d.total_users < 50).length;

    return { t1, t2, t3, t4 };
  }, [data]);

  // ─────────────────────────────────────────────
  // PARETO 80/20
  // ─────────────────────────────────────────────
  const pareto = useMemo(() => {
    let running = 0;
    let count = 0;

    for (const p of data) {
      running += p.total_users;
      count++;
      if (running >= totalUsers * 0.8) break;
    }

    return {
      count, // provinsi yang menghasilkan 80%
      percent: ((running / totalUsers) * 100).toFixed(1),
      topSum: running,
    };
  }, [data, totalUsers]);

  // OUTLIERS (must be before early return)
  const outliers = useMemo(
    () => data.filter((p) => p.total_users < 10 && p.province_name),
    [data]
  );

  // null province
  const nullEntries = useMemo(
    () => data.filter((p) => !p.province_name),
    [data]
  );

  // ─────────────────────────────────────────────
  // CHART: Top 10 Provinces
  // ─────────────────────────────────────────────
  useChart(top10Ref, [top10], () => ({
    type: 'bar',
    data: {
      labels: top10.map((p) => p.province_name ?? 'Tidak diketahui'),
      datasets: [
        {
          label: 'Total Pengguna',
          data: top10.map((p) => p.total_users),
          backgroundColor: 'rgba(13,110,253,0.6)',
          borderRadius: 6,
        },
      ],
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: { x: { beginAtZero: true } },
      plugins: { legend: { display: false } },
    },
  }));

  // ─────────────────────────────────────────────
  // CHART: Tier Distribution
  // ─────────────────────────────────────────────
  useChart(tierRef, [tiers], () => ({
    type: 'doughnut',
    data: {
      labels: [
        'Tier 1 (>300)',
        'Tier 2 (150–300)',
        'Tier 3 (50–149)',
        'Tier 4 (<50)',
      ],
      datasets: [
        {
          data: [tiers.t1, tiers.t2, tiers.t3, tiers.t4],
          backgroundColor: ['#0d6efd', '#20c997', '#ffc107', '#6c757d'],
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } },
    },
  }));

  // Early return only for empty data (not loading)
  if (!loading && (!domicilies || domicilies.length === 0)) {
    return (
      <div className="text-muted small text-center py-4">
        <i className="bi bi-inbox fs-3 d-block mb-2 opacity-50"></i>
        Tidak ada data provinsi untuk ditampilkan.
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // UI RENDER
  // ─────────────────────────────────────────────
  return (
    <div>
      {/* SUMMARY CARDS */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">Total Pengguna</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">{totalUsers}</div>
                <div className="small text-muted">
                  Total provinsi: {data.length}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">Provinsi Top 1</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">{top1?.province_name}</div>
                <div className="small text-muted">{top1?.total_users} pengguna</div>
              </>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">Pareto (80%)</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">{pareto.count} provinsi</div>
                <div className="small text-muted">{pareto.percent}% dari total</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CHART + RIGHT SIDEBAR */}
      <div className="row mb-4">
        <div className="col-lg-7 mb-3">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <h6 className="fw-semibold mb-2">
              <i className="bi bi-bar-chart-line me-2 text-primary"></i>
              Top 10 Provinsi
            </h6>
            {loading ? (
              <div className="text-center py-5" style={{ height: 360 }}>
                <div className="spinner-border text-primary" />
                <p className="text-muted mt-3 mb-0">Memuat grafik...</p>
              </div>
            ) : (
              <>
                <div style={{ height: 360 }}>
                  <canvas ref={top10Ref} />
                </div>
                <p className="chart-caption">
                  <i className="bi bi-lightbulb text-warning me-1"></i>
                  Cara baca: makin panjang batang, makin banyak peserta dari provinsi itu. Urutan sudah dari yang terbesar.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card shadow-sm rounded-4 border-0 mb-3">
            <div className="card-body">
              <h6 className="fw-semibold mb-2">
                <i className="bi bi-pie-chart me-2 text-success"></i>
                Distribusi Tier
              </h6>
              {loading ? (
                <div className="text-center py-5" style={{ height: 200 }}>
                  <div className="spinner-border text-success" />
                  <p className="text-muted mt-3 mb-0 small">Memuat distribusi...</p>
                </div>
              ) : (
                <>
                  <div style={{ height: 200 }}>
                    <canvas ref={tierRef} />
                  </div>
                  <p className="chart-caption">
                    <i className="bi bi-lightbulb text-warning me-1"></i>
                    Cara baca: provinsi dikelompokkan dari kontribusi terbesar (T1) ke terkecil (T4) berdasarkan jumlah peserta.
                  </p>
                </>
              )}
            </div>
          </div>

          {showInsight && (
            <div className="card shadow-sm rounded-4 border-0">
              <div className="card-body small text-muted">
                <h6 className="fw-semibold mb-2">
                  <i className="bi bi-lightbulb-fill text-warning me-2"></i>
                  Insight Provinsi
                </h6>

                <ul className="mb-0">
                  <li className="mb-1">
                    <strong>{top1?.province_name}</strong> menjadi penyumbang
                    terbesar ({top1?.total_users} pengguna).
                  </li>

                  <li className="mb-1">
                    {pareto.count} provinsi menghasilkan{' '}
                    <strong>{pareto.percent}%</strong> total pengguna.
                  </li>

                  <li className="mb-1">
                    Tier: T1={tiers.t1}, T2={tiers.t2}, T3={tiers.t3}, T4=
                    {tiers.t4}.
                  </li>

                  {outliers.length > 0 && (
                    <li className="mb-1 text-danger">
                      {outliers.length} provinsi memiliki kurang dari 10 pengguna.
                    </li>
                  )}

                  {nullEntries.length > 0 && (
                    <li className="mb-1">
                      {nullEntries.length} data tidak memiliki provinsi (null).
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FULL TABLE */}
      {showTable && (
        <div className="card shadow-sm rounded-4 border-0 mb-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-table me-2 text-primary"></i>
              Semua Provinsi
            </h6>

            <div className="table-responsive" style={{ maxHeight: 420 }}>
              <table className="table table-striped table-hover small">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Provinsi</th>
                    <th>Total Pengguna</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((p, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{p.province_name ?? 'Tidak diketahui'}</td>
                      <td>{p.total_users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .chart-caption { font-size: 0.75rem; color: #6c757d; margin: 10px 0 0; }
      `}</style>
    </div>
  );
}
